import React, { FC } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import {
    ProjectPricingBreakdown,
    ProjectPriceBreakdownContent,
} from "./PricingBreakdown";
import { formatCurrency } from "../../utils/formatCurrency";
import { useUser } from "../../hooks/useUser";
import { hidePricing } from "../../utils";

interface Props {
    breakdown?: ProjectPricingBreakdown | null;
    pricingSnapshottedAt?: string | null;
    /** Fallback price shown in the tooltip when no breakdown is available. */
    pricingTotal?: string | number | null;
    /**
     * The row's project (or any entity carrying brands[]/brand with rmn
     * flags). TDC-1591: RMN-deal work shows no pricing at all.
     */
    project?: any;
}

const GOLD = "var(--accent-text)";

const tooltipComponentsProps = (maxWidth: number) => ({
    tooltip: {
        sx: {
            bgcolor: "var(--surface)",
            color: "var(--text)",
            border: "1px solid var(--border)",
            borderRadius: "12px",
            boxShadow: "var(--sh-2)",
            p: "14px 16px",
            maxWidth,
        },
    },
    arrow: { sx: { color: "var(--surface)" } },
});

/**
 * Icon-only price display for list rows: a quote icon that reveals the price
 * and full line-item breakdown on hover — dollar amounts never sit inline in
 * the list. Projects under a packaged SOW get a package icon instead, and the
 * tooltip shows only the engagement terms: the à-la-carte quote math is not
 * shown at all for packaged work.
 */
export const PriceBreakdownTooltip: FC<Props> = ({
    breakdown,
    pricingSnapshottedAt,
    pricingTotal,
    project,
}) => {
    const { user, role } = useUser();

    // The only viewers past here are internal staff on non-RMN work:
    // TDC-1591 hides RMN-deal pricing from everyone, and TDC-1578 hides all
    // pricing from brand-side viewers — package terms and locked "actual"
    // prices included.
    if (hidePricing(role, user, project)) {
        return null;
    }

    // Packaged SOW: package terms only — no à-la-carte figures for anyone.
    if (breakdown?.package) {
        const pkg = breakdown.package;
        return (
            <Tooltip
                arrow
                placement="left"
                enterDelay={200}
                title={
                    <Box sx={{ py: 0.5 }}>
                        <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>
                            {pkg.name} package
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
                            Covered by the {pkg.name} package —{" "}
                            {formatCurrency(pkg.sellPrice)} for the whole engagement.
                            This project is not billed individually.
                        </Typography>
                    </Box>
                }
                componentsProps={tooltipComponentsProps(340)}
            >
                <Inventory2OutlinedIcon
                    sx={{ fontSize: 20, color: GOLD, cursor: "help" }}
                />
            </Tooltip>
        );
    }

    if (!breakdown && (pricingTotal === null || pricingTotal === undefined)) {
        return null;
    }

    return (
        <Tooltip
            arrow
            placement="left"
            enterDelay={200}
            title={
                breakdown ? (
                    <Box sx={{ minWidth: 360, py: 0.5 }}>
                        <ProjectPriceBreakdownContent
                            breakdown={breakdown}
                            pricingSnapshottedAt={pricingSnapshottedAt}
                        />
                    </Box>
                ) : (
                    <Box sx={{ py: 0.5 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
                            {formatCurrency(pricingTotal)}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "var(--muted)", fontStyle: "italic" }}>
                            Breakdown unavailable.
                        </Typography>
                    </Box>
                )
            }
            componentsProps={tooltipComponentsProps(breakdown ? 480 : 340)}
        >
            <RequestQuoteOutlinedIcon
                sx={{ fontSize: 20, color: "var(--accent-text)", cursor: "help" }}
            />
        </Tooltip>
    );
};
